// Money-room HTTP API (protocol §3, money-mode §12.2), client side:
// - `fetchMoneyInfo`   GET /api/info → its `money` block (stakes, label price, pools with the
//                      head count shown before the buy-in), or null while money rooms are off.
// - `findMoneyRoom`    GET /api/money/find?stake= → ok { roomId, wsUrl, humans, maxPlayers } |
//                      full | disabled (404).
// - `fetchMoneyStatus` GET /api/money/status → the signed-in account's live / frozen money life
//                      and its last exit (checked before every buy-in; polled after a drop).
// - `requestDevTicket` POST /api/money/ticket { stakeUsd } → ok { ticket, quote } |
//                      error { error, httpStatus }. DEV ONLY: the server answers 404 unless money is
//                      on with the in-memory fake settlement (never in production, where
//                      money.enabled = false). The real wallet buy-in replaces this one call;
//                      PLAY with `ticket.id` does not change.
// Sign-in (protocol §3.2): both send `Authorization: Bearer <access token>` and no account id —
// the server takes the account from the verified token. `find` stays anonymous. A refused token
// comes back as `authRequired` / `authInvalid` (401) or `banned` (403); the caller
// (auth/session.ts `authed`) retries once with a fresh token.
//
// Money flow (app.ts `MoneyFlow`): stake select (fetchMoneyInfo pools) → buy-in
// (requestDevTicket) → find + connect (findMoneyRoom; the Connector needs a money target) →
// PLAY with the ticket (`GameSocket.sendPlay(nick, skin, ticket.id)`).
//
// Response shapes are identical to server/src/http/api.ts (ApiMoneyInfo, ApiMoneyFindResponse,
// ApiMoneyTicketResponse). Every function validates the body and never trusts amounts beyond
// "a safe non-negative integer".
//
// Implemented fully (contracts step). DOM-free (uses `fetch`). Consumers: app.ts money flow, the
// money menu UI (stake select).

import { isLamports, NET_TIMING } from '@ballio/shared';
import { serverUrl } from './server-url.js';

export interface MoneyHttpOptions {
  /** Server base (net/server-url.ts); `""` = this page. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** Abort after this many ms. Default 5000 (protocol §3). */
  readonly timeoutMs?: number;
}

export interface ApiMoneyPool {
  readonly stakeUsd: number;
  /** Humans playing over the stake's rooms. */
  readonly humans: number;
  readonly rooms: number;
  /** Humans in the room /api/money/find would return now (shown before the buy-in). */
  readonly joinHumans: number;
  readonly maxPlayers: number;
  /** Every room of the stake is full and no new one may open. */
  readonly full: boolean;
}

export interface ApiMoneyInfo {
  readonly enabled: boolean;
  /** money.stakes (USD). */
  readonly stakes: readonly number[];
  /** Label price, USD per SOL; 0 = unknown. */
  readonly solUsd: number;
  readonly labelCurrency: 'USD' | 'SOL';
  /** Fraction kept by the house, 0–1. */
  readonly rake: number;
  /** POST /api/money/ticket (dev fake buy-in) is available. */
  readonly devBuyIn: boolean;
  /** The server's capacity guard admits money finds (true when an older server leaves it out). */
  readonly available: boolean;
  readonly pools: readonly ApiMoneyPool[];
  /** The game money rooms run: the territory arena or the cell game (older servers: cells). */
  readonly game: 'arena' | 'cells';
}

export type ApiMoneyFindResult =
  | {
      readonly status: 'ok';
      readonly roomId: string;
      readonly wsUrl: string;
      readonly humans: number;
      readonly maxPlayers: number;
    }
  /** Wait `retryAfterSec` (when given) and ask again; `reason: 'capacity'` = server at budget. */
  | { readonly status: 'full'; readonly retryAfterSec?: number; readonly reason?: 'capacity' }
  /** 404: money rooms are off, or the stake is not offered. */
  | { readonly status: 'disabled' };

export interface ApiMoneyTicket {
  /** The bearer id PLAY carries (≤ 64 bytes). */
  readonly id: string;
  readonly account: string;
  readonly stakeUsd: number;
  readonly lamportsPaid: number;
  readonly solUsd: number;
  readonly issuedAt: number;
}

export interface ApiMoneyQuote {
  readonly stakeUsd: number;
  readonly solUsd: number;
  /** Lamports paid. */
  readonly lamports: number;
  readonly rake: number;
  /** Lamports the life spawns with. */
  readonly entryValue: number;
}

/** Server error codes of POST /api/money/ticket, plus client-side failures. */
export type MoneyTicketError =
  | 'badRequest'
  | 'invalidAccount'
  | 'paymentFailed'
  | 'stakeNotOffered'
  | 'rateLimited'
  | 'priceUnavailable'
  | 'shuttingDown'
  /** 409: the account's payment wallet was unlinked in Privy (support must look first). */
  | 'walletUnlinked'
  /** 404: no dev buy-in on this server (money off, or a real settlement). */
  | 'unavailable'
  /** 401: no / a refused access token (sign in again). */
  | 'authRequired'
  | 'authInvalid'
  /** 403: the account is banned. */
  | 'banned'
  /** Network error, timeout or a malformed answer. */
  | 'network';

export type ApiMoneyTicketResult =
  | { readonly status: 'ok'; readonly ticket: ApiMoneyTicket; readonly quote: ApiMoneyQuote }
  | { readonly status: 'error'; readonly error: MoneyTicketError; readonly httpStatus: number };

const TICKET_ERRORS: readonly MoneyTicketError[] = [
  'badRequest',
  'invalidAccount',
  'paymentFailed',
  'stakeNotOffered',
  'rateLimited',
  'priceUnavailable',
  'shuttingDown',
  'walletUnlinked',
  'authRequired',
  'authInvalid',
  'banned',
];

/** The 401 / 403 auth refusals of the money routes (shapes in @ballio/shared account.ts). */
export type MoneyAuthError = 'authRequired' | 'authInvalid' | 'banned' | 'authUnavailable';

function authErrorOf(status: number, body: unknown): MoneyAuthError | null {
  if (status !== 401 && status !== 403 && status !== 503) return null;
  const code = asRecord(body)?.['error'];
  return code === 'authRequired' ||
    code === 'authInvalid' ||
    code === 'banned' ||
    code === 'authUnavailable'
    ? code
    : status === 401
      ? 'authInvalid'
      : null;
}

function bearer(token: string): Record<string, string> {
  return { authorization: `Bearer ${token}` };
}

/** A transport failure or an answer the endpoint does not define. */
export class MoneyHttpError extends Error {
  readonly status: number;
  /** 401 / 403: why the server refused the access token. */
  readonly auth: MoneyAuthError | null;

  constructor(message: string, status: number, auth: MoneyAuthError | null = null) {
    super(message);
    this.name = 'MoneyHttpError';
    this.status = status;
    this.auth = auth;
  }
}

// --- plumbing ---------------------------------------------------------------------------------

interface HttpResult {
  readonly status: number;
  readonly ok: boolean;
  readonly body: unknown;
}

async function request(
  path: string,
  opts: MoneyHttpOptions | undefined,
  init?: RequestInit,
): Promise<HttpResult> {
  const url = serverUrl(opts?.baseUrl ?? '', path);
  const doFetch: typeof fetch =
    opts?.fetchImpl ?? ((input, reqInit) => globalThis.fetch(input, reqInit));
  const timeoutMs = opts?.timeoutMs ?? NET_TIMING.connectAttemptTimeoutSec * 1000;
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  let handle: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    handle = setTimeout(() => {
      ctrl?.abort();
      reject(new MoneyHttpError(`${path}: timed out after ${timeoutMs} ms`, 0));
    }, timeoutMs);
  });
  try {
    const res = await Promise.race([
      doFetch(url, { ...init, signal: ctrl?.signal ?? null }),
      timeout,
    ]);
    let body: unknown = undefined;
    try {
      body = await Promise.race([res.json() as Promise<unknown>, timeout]);
    } catch {
      body = undefined;
    }
    return { status: res.status, ok: res.ok, body };
  } finally {
    if (handle !== null) clearTimeout(handle);
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function count(v: unknown): number {
  const n = num(v);
  return n > 0 ? Math.floor(n) : 0;
}

function lamports(v: unknown): number | null {
  return isLamports(v) ? v : null;
}

// --- parsing ----------------------------------------------------------------------------------

/** Validates the `money` block of /api/info; null when absent or malformed. */
export function parseMoneyInfo(raw: unknown): ApiMoneyInfo | null {
  const b = asRecord(raw);
  if (!b) return null;
  const stakes: number[] = [];
  if (Array.isArray(b['stakes'])) {
    for (const s of b['stakes'] as unknown[]) if (num(s) > 0) stakes.push(num(s));
  }
  const pools: ApiMoneyPool[] = [];
  if (Array.isArray(b['pools'])) {
    for (const p of b['pools'] as unknown[]) {
      const o = asRecord(p);
      if (!o || !(num(o['stakeUsd']) > 0)) continue;
      pools.push({
        stakeUsd: num(o['stakeUsd']),
        humans: count(o['humans']),
        rooms: count(o['rooms']),
        joinHumans: count(o['joinHumans']),
        maxPlayers: count(o['maxPlayers']),
        full: o['full'] === true,
      });
    }
  }
  const rake = num(b['rake']);
  return {
    enabled: b['enabled'] === true,
    stakes,
    solUsd: Math.max(0, num(b['solUsd'])),
    labelCurrency: b['labelCurrency'] === 'SOL' ? 'SOL' : 'USD',
    rake: rake >= 0 && rake < 1 ? rake : 0,
    devBuyIn: b['devBuyIn'] === true,
    game: b['game'] === 'arena' ? 'arena' : 'cells',
    available: b['available'] !== false,
    pools,
  };
}

function parseTicket(raw: unknown): ApiMoneyTicket | null {
  const o = asRecord(raw);
  if (!o) return null;
  const id = o['id'];
  const paid = lamports(o['lamportsPaid']);
  if (typeof id !== 'string' || id === '' || paid === null) return null;
  return {
    id,
    account: typeof o['account'] === 'string' ? o['account'] : '',
    stakeUsd: num(o['stakeUsd']),
    lamportsPaid: paid,
    solUsd: Math.max(0, num(o['solUsd'])),
    issuedAt: num(o['issuedAt']),
  };
}

function parseQuote(raw: unknown): ApiMoneyQuote | null {
  const o = asRecord(raw);
  if (!o) return null;
  const paid = lamports(o['lamports']);
  const entry = lamports(o['entryValue']);
  if (paid === null || entry === null) return null;
  return {
    stakeUsd: num(o['stakeUsd']),
    solUsd: Math.max(0, num(o['solUsd'])),
    lamports: paid,
    rake: num(o['rake']),
    entryValue: entry,
  };
}

// --- endpoints --------------------------------------------------------------------------------

/** GET /api/info → its `money` block, or null while money rooms are off. Rejects on failures. */
export async function fetchMoneyInfo(opts?: MoneyHttpOptions): Promise<ApiMoneyInfo | null> {
  const res = await request('/api/info', opts);
  if (!res.ok) throw new MoneyHttpError(`/api/info: HTTP ${res.status}`, res.status);
  const b = asRecord(res.body);
  if (!b) throw new MoneyHttpError('/api/info: not a JSON object', res.status);
  return parseMoneyInfo(b['money']);
}

/** GET /api/money/find?stake= (200 ok/full, 404 disabled). Rejects on other failures. */
export async function findMoneyRoom(
  stakeUsd: number,
  opts?: MoneyHttpOptions,
): Promise<ApiMoneyFindResult> {
  const res = await request(`/api/money/find?stake=${encodeURIComponent(String(stakeUsd))}`, opts);
  const b = asRecord(res.body);
  if (res.status === 404) return { status: 'disabled' };
  if (b?.['status'] === 'full') {
    const out: { status: 'full'; retryAfterSec?: number; reason?: 'capacity' } = { status: 'full' };
    const wait = b['retryAfterSec'];
    if (typeof wait === 'number' && Number.isFinite(wait) && wait > 0) out.retryAfterSec = wait;
    if (b['reason'] === 'capacity') out.reason = 'capacity';
    return out;
  }
  if (!res.ok) throw new MoneyHttpError(`/api/money/find: HTTP ${res.status}`, res.status);
  const roomId = b?.['roomId'];
  const wsUrl = b?.['wsUrl'];
  if (b?.['status'] !== 'ok' || typeof roomId !== 'string' || typeof wsUrl !== 'string' || !wsUrl) {
    throw new MoneyHttpError('/api/money/find: malformed response', res.status);
  }
  return {
    status: 'ok',
    roomId,
    wsUrl,
    humans: count(b['humans']),
    maxPlayers: count(b['maxPlayers']),
  };
}

/**
 * GET /api/practice/find (money-mode §1.4; no sign-in): the practice room to join and its head
 * count. 404 → `disabled` (practice off on this server); `full` → wait and ask again.
 */
export async function findPracticeRoom(opts?: MoneyHttpOptions): Promise<ApiMoneyFindResult> {
  const res = await request('/api/practice/find', opts);
  const b = asRecord(res.body);
  if (res.status === 404) return { status: 'disabled' };
  if (b?.['status'] === 'full') {
    const out: { status: 'full'; retryAfterSec?: number; reason?: 'capacity' } = { status: 'full' };
    const wait = b['retryAfterSec'];
    if (typeof wait === 'number' && Number.isFinite(wait) && wait > 0) out.retryAfterSec = wait;
    if (b['reason'] === 'capacity') out.reason = 'capacity';
    return out;
  }
  if (!res.ok) throw new MoneyHttpError(`/api/practice/find: HTTP ${res.status}`, res.status);
  const roomId = b?.['roomId'];
  const wsUrl = b?.['wsUrl'];
  if (b?.['status'] !== 'ok' || typeof roomId !== 'string' || typeof wsUrl !== 'string' || !wsUrl) {
    throw new MoneyHttpError('/api/practice/find: malformed response', res.status);
  }
  return {
    status: 'ok',
    roomId,
    wsUrl,
    humans: count(b['humans']),
    maxPlayers: count(b['maxPlayers']),
  };
}

/**
 * POST /api/money/ticket (dev fake buy-in). Never rejects: transport failures come back as
 * `{ status: 'error', error: 'network' }`.
 */
export async function requestDevTicket(
  token: string,
  stakeUsd: number,
  opts?: MoneyHttpOptions,
): Promise<ApiMoneyTicketResult> {
  let res: HttpResult;
  try {
    res = await request('/api/money/ticket', opts, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...bearer(token) },
      body: JSON.stringify({ stakeUsd }),
    });
  } catch {
    return { status: 'error', error: 'network', httpStatus: 0 };
  }
  const b = asRecord(res.body);
  if (res.ok && b?.['status'] === 'ok') {
    const ticket = parseTicket(b['ticket']);
    const quote = parseQuote(b['quote']);
    if (ticket && quote) return { status: 'ok', ticket, quote };
    return { status: 'error', error: 'network', httpStatus: res.status };
  }
  if (res.status === 404) return { status: 'error', error: 'unavailable', httpStatus: 404 };
  const auth = authErrorOf(res.status, res.body);
  if (auth !== null && auth !== 'authUnavailable') {
    return { status: 'error', error: auth, httpStatus: res.status };
  }
  const code = b?.['error'];
  const error = (TICKET_ERRORS as readonly unknown[]).includes(code)
    ? (code as MoneyTicketError)
    : 'network';
  return { status: 'error', error, httpStatus: res.status };
}

/** GET /api/money/status: the account's money life now, and how its last one ended. */
export interface ApiMoneyStatus {
  readonly life: {
    readonly roomId: string;
    readonly stakeUsd: number;
    /** Admitted, not spawned yet. */
    readonly reserved: boolean;
    /** Lamports held now. */
    readonly value: number;
    readonly frozen: boolean;
    readonly frozenReason: 'disconnect' | 'idle' | null;
    /** Whole seconds until a frozen life's automatic cash-out. */
    readonly autoCashOutInSec: number;
  } | null;
  readonly lastExit: {
    readonly reason: 'death' | 'cashout' | 'disconnect' | 'idle' | 'shutdown';
    /** Lamports paid out (0 for a death). */
    readonly paid: number;
    readonly value: number;
    readonly stakeUsd: number;
    /** Wall ms (server clock). */
    readonly atMs: number;
  } | null;
}

const EXIT_REASONS = ['death', 'cashout', 'disconnect', 'idle', 'shutdown'] as const;

/** Validates a /api/money/status body; null when malformed. */
export function parseMoneyStatus(raw: unknown): ApiMoneyStatus | null {
  const b = asRecord(raw);
  if (!b || b['status'] !== 'ok') return null;
  let life: ApiMoneyStatus['life'] = null;
  const l = asRecord(b['life']);
  if (l) {
    const fr = l['frozenReason'];
    life = {
      roomId: typeof l['roomId'] === 'string' ? l['roomId'] : '',
      stakeUsd: Math.max(0, num(l['stakeUsd'])),
      reserved: l['reserved'] === true,
      value: lamports(l['value']) ?? 0,
      frozen: l['frozen'] === true,
      frozenReason: fr === 'idle' || fr === 'disconnect' ? fr : null,
      autoCashOutInSec: count(l['autoCashOutInSec']),
    };
  }
  let lastExit: ApiMoneyStatus['lastExit'] = null;
  const e = asRecord(b['lastExit']);
  const reason = e?.['reason'];
  if (e && (EXIT_REASONS as readonly unknown[]).includes(reason)) {
    lastExit = {
      reason: reason as (typeof EXIT_REASONS)[number],
      paid: lamports(e['paid']) ?? 0,
      value: lamports(e['value']) ?? 0,
      stakeUsd: Math.max(0, num(e['stakeUsd'])),
      atMs: num(e['atMs']),
    };
  }
  return { life, lastExit };
}

/**
 * GET /api/money/status with the Bearer token (protocol §3). Resolves null when money rooms are
 * off (404); rejects on transport failures, auth refusals (`MoneyHttpError.auth`) and malformed
 * answers.
 */
export async function fetchMoneyStatus(
  token: string,
  opts?: MoneyHttpOptions,
): Promise<ApiMoneyStatus | null> {
  const res = await request('/api/money/status', opts, { headers: bearer(token) });
  if (res.status === 404) return null;
  if (!res.ok) {
    const auth = authErrorOf(res.status, res.body);
    throw new MoneyHttpError(`/api/money/status: HTTP ${res.status}`, res.status, auth);
  }
  const parsed = parseMoneyStatus(res.body);
  if (parsed === null)
    throw new MoneyHttpError('/api/money/status: malformed response', res.status);
  return parsed;
}
